import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { contentAPI } from '../../services/api';
import { formatMarathiDate, formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './NoticesPage.css';

const PRIORITY_CONFIG = {
  urgent:    { label: 'अत्यंत तातडीचे', color: '#dc2626', bg: '#fee2e2', border: '#ef4444', icon: '🚨', order: 1 },
  important: { label: 'महत्त्वाचे',     color: '#ea580c', bg: '#ffedd5', border: '#f97316', icon: '⚡', order: 2 },
  normal:    { label: 'सामान्य',         color: '#15803d', bg: '#dcfce7', border: '#22c55e', icon: '📢', order: 3 },
  info:      { label: 'माहिती',          color: '#2563eb', bg: '#eff6ff', border: '#3b82f6', icon: 'ℹ️', order: 4 },
};

const CATEGORY_ICONS = {
  'सर्व': '📁',
  'सामान्य': '📋',
  'निविदा': '📑',
  'सभा': '🏛️',
  'रोजगार': '💼',
  'घोषणा': '📢',
  'सुट्टी': '🏖️',
  'पाणीपुरवठा': '💧',
  'आरोग्य': '💉',
  'इतर': '📌',
};

const CATEGORIES = ['सर्व', 'सामान्य', 'निविदा', 'सभा', 'रोजगार', 'घोषणा', 'सुट्टी', 'इतर'];

const isNoticeExpired = (expiryDate) => {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expiry < today;
};

const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const isImageFile = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|webp|gif)($|\?)/i.test(url);
};

export default function NoticesPage() {
  const { tenantSlug } = useParams();
  const { tenantData, loading: tenantLoading } = useTenant();
  const [notices, setNotices] = useState(tenantData?.notices || []);
  const [loading, setLoading] = useState(!tenantData?.notices?.length && tenantLoading);
  const [error, setError] = useState(null);

  // Filters & Sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('सर्व');
  const [selectedCategory, setSelectedCategory] = useState('सर्व');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'expired'
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'expiry' | 'priority' | 'oldest'

  // Modal state
  const [selectedNotice, setSelectedNotice] = useState(null);

  const fetchNotices = () => {
    if (!tenantSlug) return;
    setLoading(true);
    setError(null);
    contentAPI.getPublicNotices(tenantSlug)
      .then(res => {
        setNotices(res.data.notices || []);
      })
      .catch(err => {
        console.error('Failed to load notices:', err);
        setError('सूचना लोड करण्यात अडचण आली.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tenantData?.notices) {
      setNotices(tenantData.notices);
      setLoading(false);
    } else {
      fetchNotices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantData, tenantSlug]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedNotice) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedNotice]);

  const filteredAndSortedNotices = useMemo(() => {
    return notices
      .filter(n => {
        // Priority filter
        const priOk = selectedPriority === 'सर्व' || n.priority === selectedPriority;

        // Category filter
        const catOk = selectedCategory === 'सर्व' || n.category === selectedCategory;

        // Status filter (Active vs Expired)
        const expired = isNoticeExpired(n.expiry_date);
        let statusOk = true;
        if (statusFilter === 'active') statusOk = !expired;
        if (statusFilter === 'expired') statusOk = expired;

        // Search filter
        let searchOk = true;
        if (searchTerm.trim()) {
          const q = searchTerm.toLowerCase();
          const titleMatch = (n.title || '').toLowerCase().includes(q);
          const contentMatch = (n.content || '').toLowerCase().includes(q);
          const categoryMatch = (n.category || '').toLowerCase().includes(q);
          searchOk = titleMatch || contentMatch || categoryMatch;
        }

        return priOk && catOk && statusOk && searchOk;
      })
      .sort((a, b) => {
        if (sortBy === 'newest') {
          const dateA = new Date(a.published_date || a.publish_date || a.createdAt || 0).getTime();
          const dateB = new Date(b.published_date || b.publish_date || b.createdAt || 0).getTime();
          return dateB - dateA;
        }
        if (sortBy === 'oldest') {
          const dateA = new Date(a.published_date || a.publish_date || a.createdAt || 0).getTime();
          const dateB = new Date(b.published_date || b.publish_date || b.createdAt || 0).getTime();
          return dateA - dateB;
        }
        if (sortBy === 'expiry') {
          if (!a.expiry_date && !b.expiry_date) return 0;
          if (!a.expiry_date) return 1;
          if (!b.expiry_date) return -1;
          return new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime();
        }
        if (sortBy === 'priority') {
          const orderA = PRIORITY_CONFIG[a.priority]?.order || 99;
          const orderB = PRIORITY_CONFIG[b.priority]?.order || 99;
          return orderA - orderB;
        }
        return 0;
      });
  }, [notices, selectedPriority, selectedCategory, statusFilter, searchTerm, sortBy]);

  const categoryCounts = useMemo(() => {
    return CATEGORIES.reduce((acc, cat) => {
      acc[cat] = cat === 'सर्व' ? notices.length : notices.filter(n => n.category === cat).length;
      return acc;
    }, {});
  }, [notices]);

  const countsByStatus = useMemo(() => {
    const active = notices.filter(n => !isNoticeExpired(n.expiry_date)).length;
    const expired = notices.filter(n => isNoticeExpired(n.expiry_date)).length;
    return { all: notices.length, active, expired };
  }, [notices]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedPriority('सर्व');
    setSelectedCategory('सर्व');
    setStatusFilter('all');
    setSortBy('newest');
  };

  const handleWhatsAppShare = (notice) => {
    const title = notice.title || 'ग्रामपंचायत नोटीस';
    const text = `📢 *ग्रामपंचायत महत्त्वाची सूचना*\n\n📌 *विषय:* ${title}\n📅 *तारीख:* ${formatMarathiDate(notice.published_date || notice.publish_date || notice.createdAt)}\n\nअधिक माहितीसाठी गावाच्या अधिकृत डिजिटल पोर्टलला भेट द्या:\n${window.location.href}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  if (loading) return <LoadingSpinner fullPage message="सूचना व घोषणा लोड होत आहेत..." />;
  if (error && !notices.length) {
    return (
      <div className="notices-page" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <p style={{ color: '#dc2626', marginBottom: 16 }}>{error}</p>
        <button onClick={fetchNotices} className="btn-primary" style={{ cursor: 'pointer', padding: '8px 20px', borderRadius: 6 }}>
          पुन्हा प्रयत्न करा
        </button>
      </div>
    );
  }

  return (
    <div className="notices-page">
      {/* Hero Banner */}
      <section className="notices-hero">
        <div className="notices-hero-content">
          <div className="notices-hero-badge">
            <span>📢 अधिकृत जाहीर सूचना फलक</span>
          </div>
          <h1>ग्रामपंचायत सूचना व घोषणा</h1>
          <p className="notices-hero-desc">
            ग्रामपंचायतीमार्फत प्रसिद्ध करण्यात येणाऱ्या सर्व जाहीर सूचना, परिपत्रके, निविदा आणि सभेची विषयपत्रिका.
          </p>
          <div className="notices-hero-stats-row">
            <div className="hero-stat-pill">
              <strong>{formatMarathiNumber(countsByStatus.all)}</strong>
              <span>एकूण सूचना</span>
            </div>
            <div className="hero-stat-pill active">
              <strong>{formatMarathiNumber(countsByStatus.active)}</strong>
              <span>सक्रिय सूचना</span>
            </div>
            <div className="hero-stat-pill expired">
              <strong>{formatMarathiNumber(countsByStatus.expired)}</strong>
              <span>मुदत संपलेल्या</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Body */}
      <div className="notices-body">
        {/* Sidebar Filters */}
        <aside className="notices-sidebar">
          {/* Status Tabs */}
          <div className="notices-filter-box">
            <h4>स्थिती (Status)</h4>
            <div className="notices-status-tabs">
              <button
                className={`status-tab-btn ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                <span>सर्व</span>
                <b className="tab-count">{formatMarathiNumber(countsByStatus.all)}</b>
              </button>
              <button
                className={`status-tab-btn active-tab ${statusFilter === 'active' ? 'active' : ''}`}
                onClick={() => setStatusFilter('active')}
              >
                <span>🟢 चालू</span>
                <b className="tab-count">{formatMarathiNumber(countsByStatus.active)}</b>
              </button>
              <button
                className={`status-tab-btn expired-tab ${statusFilter === 'expired' ? 'active' : ''}`}
                onClick={() => setStatusFilter('expired')}
              >
                <span>⚪ मुदत संपलेल्या</span>
                <b className="tab-count">{formatMarathiNumber(countsByStatus.expired)}</b>
              </button>
            </div>
          </div>

          {/* Priority Box */}
          <div className="notices-filter-box">
            <h4>प्राधान्यक्रम (Priority)</h4>
            {['सर्व', ...Object.keys(PRIORITY_CONFIG)].map(p => {
              const cfg = PRIORITY_CONFIG[p];
              const count = p === 'सर्व' ? notices.length : notices.filter(n => n.priority === p).length;
              return (
                <button
                  key={p}
                  className={`notices-filter-btn ${selectedPriority === p ? 'active' : ''}`}
                  style={selectedPriority === p && p !== 'सर्व' ? {
                    background: cfg?.bg,
                    borderColor: cfg?.border,
                    color: cfg?.color,
                  } : {}}
                  onClick={() => setSelectedPriority(p)}
                >
                  <span className="btn-label">
                    {p === 'सर्व' ? '🔔 सर्व प्राधान्य' : `${cfg?.icon} ${cfg?.label}`}
                  </span>
                  <span className="nf-count">{formatMarathiNumber(count)}</span>
                </button>
              );
            })}
          </div>

          {/* Category Box */}
          <div className="notices-filter-box">
            <h4>श्रेणी (Category)</h4>
            {CATEGORIES.filter(c => categoryCounts[c] > 0 || c === 'सर्व').map(cat => (
              <button
                key={cat}
                className={`notices-filter-btn ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                <span className="btn-label">
                  {CATEGORY_ICONS[cat] || '📌'} {cat === 'सर्व' ? 'सर्व श्रेणी' : cat}
                </span>
                <span className="nf-count">{formatMarathiNumber(categoryCounts[cat] || 0)}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Notices Main Area */}
        <main className="notices-main">
          {/* Controls Bar: Search & Sort */}
          <div className="notices-controls-card">
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="notices-search-input"
                placeholder="सूचना, विषय किंवा मजकूर शोधा..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="search-clear-btn"
                  onClick={() => setSearchTerm('')}
                  title="शोध पुसा"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="sort-controls-wrapper">
              <span className="sort-label">क्रमवारी:</span>
              <select
                className="notices-sort-select"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="newest">📅 नवीनतम प्रथम (Newest)</option>
                <option value="expiry">⏳ मुदत समाप्ती जवळ (Expiring)</option>
                <option value="priority">🚨 अत्यंत तातडीचे प्रथम (Urgent)</option>
                <option value="oldest">📆 जुने प्रथम (Oldest)</option>
              </select>
            </div>
          </div>

          {/* Active Filter Chips Bar (if any filter active) */}
          {(searchTerm || selectedPriority !== 'सर्व' || selectedCategory !== 'सर्व' || statusFilter !== 'all') && (
            <div className="active-filter-bar">
              <span className="af-summary">
                {formatMarathiNumber(filteredAndSortedNotices.length)} सूचना सापडल्या:
              </span>
              <div className="af-chips-row">
                {searchTerm && (
                  <span className="af-chip">
                    शोध: "{searchTerm}"
                    <button onClick={() => setSearchTerm('')}>✕</button>
                  </span>
                )}
                {selectedPriority !== 'सर्व' && (
                  <span className="af-chip">
                    प्राधान्य: {PRIORITY_CONFIG[selectedPriority]?.label}
                    <button onClick={() => setSelectedPriority('सर्व')}>✕</button>
                  </span>
                )}
                {selectedCategory !== 'सर्व' && (
                  <span className="af-chip">
                    श्रेणी: {selectedCategory}
                    <button onClick={() => setSelectedCategory('सर्व')}>✕</button>
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="af-chip">
                    {statusFilter === 'active' ? 'चालू सूचना' : 'मुदत संपलेल्या'}
                    <button onClick={() => setStatusFilter('all')}>✕</button>
                  </span>
                )}
                <button className="af-reset-btn" onClick={resetFilters}>
                  सर्व फिल्टर्स पुसा (Reset)
                </button>
              </div>
            </div>
          )}

          {/* Notices Cards List */}
          {filteredAndSortedNotices.length === 0 ? (
            <div className="notices-empty-card">
              <div className="empty-icon">📭</div>
              <h3>कोणतीही सूचना आढळली नाही</h3>
              <p>निवडलेल्या निकषांनुसार सध्या एकही सूचना उपलब्ध नाही.</p>
              <button className="btn-reset-empty" onClick={resetFilters}>
                सर्व सूचना पहा
              </button>
            </div>
          ) : (
            <div className="notices-cards-grid">
              {filteredAndSortedNotices.map(n => {
                const pri = PRIORITY_CONFIG[n.priority] || PRIORITY_CONFIG.normal;
                const expired = isNoticeExpired(n.expiry_date);
                const daysLeft = getDaysUntilExpiry(n.expiry_date);
                const isImage = isImageFile(n.attachment_url);

                return (
                  <article
                    key={n.id}
                    className={`modern-notice-card ${expired ? 'card-expired' : ''}`}
                    style={{ borderLeftColor: pri.border }}
                  >
                    {/* Top Row: Meta Tags & Badges */}
                    <div className="card-top-row">
                      <div className="card-tags-left">
                        {n.category && (
                          <span className="category-pill">
                            {CATEGORY_ICONS[n.category] || '📌'} {n.category.replace(/_/g, ' ')}
                          </span>
                        )}
                        <span
                          className="priority-pill"
                          style={{ background: pri.bg, color: pri.color, borderColor: pri.border }}
                        >
                          {pri.icon} {pri.label}
                        </span>
                      </div>

                      <div className="card-status-right">
                        {expired ? (
                          <span className="expiry-badge expired">
                            ⚪ मुदत संपली
                          </span>
                        ) : daysLeft != null ? (
                          <span className={`expiry-badge ${daysLeft <= 2 ? 'critical' : 'active'}`}>
                            ⏳ {daysLeft === 0 ? 'आज शेवटचा दिवस' : `${toMarathiDigits(daysLeft)} दिवस शिल्लक`}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {/* Notice Title */}
                    <h2
                      className="card-title"
                      onClick={() => setSelectedNotice(n)}
                      title="सविस्तर वाचण्यासाठी क्लिक करा"
                    >
                      {n.title}
                    </h2>

                    {/* Notice Content Snippet */}
                    {n.content && (
                      <p className="card-snippet">
                        {n.content}
                      </p>
                    )}

                    {/* Attachment mini-banner if present */}
                    {n.attachment_url && (
                      <div className="card-attachment-banner">
                        <span className="att-icon">{isImage ? '🖼️' : '📎'}</span>
                        <span className="att-text">
                          {isImage ? 'अधिकृत छायाचित्र जोडले आहे' : 'अधिकृत परिपत्रक / दस्तऐवज (PDF) जोडले आहे'}
                        </span>
                        <a
                          href={n.attachment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="att-quick-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          डाऊनलोड ↗
                        </a>
                      </div>
                    )}

                    {/* Card Footer Row */}
                    <div className="card-footer-row">
                      <div className="footer-dates-group">
                        <span className="footer-date" title="प्रकाशन दिनांक">
                          📅 {formatMarathiDate(n.published_date || n.publish_date || n.createdAt)}
                        </span>
                        {n.expiry_date && (
                          <span className="footer-date expiry" title="मुदत समाप्ती">
                            ⏰ मुदत: {formatMarathiDate(n.expiry_date)}
                          </span>
                        )}
                      </div>

                      <div className="footer-actions-group">
                        <button
                          className="action-btn share-btn"
                          onClick={() => handleWhatsAppShare(n)}
                          title="WhatsApp वर शेअर करा"
                        >
                          📲 शेअर
                        </button>
                        <button
                          className="action-btn detail-btn"
                          onClick={() => setSelectedNotice(n)}
                        >
                          सविस्तर वाचा →
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Interactive Detail Modal */}
      {selectedNotice && (
        <div className="notice-modal-backdrop" onClick={() => setSelectedNotice(null)}>
          <div className="notice-modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-tags">
                <span className="category-pill">
                  {CATEGORY_ICONS[selectedNotice.category] || '📌'} {selectedNotice.category || 'सूचना'}
                </span>
                {selectedNotice.priority && (
                  <span
                    className="priority-pill"
                    style={{
                      background: PRIORITY_CONFIG[selectedNotice.priority]?.bg,
                      color: PRIORITY_CONFIG[selectedNotice.priority]?.color,
                    }}
                  >
                    {PRIORITY_CONFIG[selectedNotice.priority]?.icon} {PRIORITY_CONFIG[selectedNotice.priority]?.label}
                  </span>
                )}
                {isNoticeExpired(selectedNotice.expiry_date) && (
                  <span className="expiry-badge expired">⚪ मुदत संपलेली सूचना</span>
                )}
              </div>
              <button
                className="modal-close-btn"
                onClick={() => setSelectedNotice(null)}
                aria-label="बंद करा"
              >
                ✕
              </button>
            </div>

            <div className="modal-scroll-body">
              <h2 className="modal-title">{selectedNotice.title}</h2>

              <div className="modal-meta-bar">
                <div className="meta-item">
                  <small>प्रकाशन दिनांक</small>
                  <strong>📅 {formatMarathiDate(selectedNotice.published_date || selectedNotice.publish_date || selectedNotice.createdAt)}</strong>
                </div>
                {selectedNotice.expiry_date && (
                  <div className="meta-item">
                    <small>मुदत समाप्ती</small>
                    <strong>⏰ {formatMarathiDate(selectedNotice.expiry_date)}</strong>
                  </div>
                )}
                <div className="meta-item">
                  <small>ग्रामपंचायत</small>
                  <strong>🏛️ {tenantData?.name || 'ग्रामपंचायत'}</strong>
                </div>
              </div>

              {/* Full Notice Content */}
              <div className="modal-notice-content">
                <h4>📜 सूचनेचा सविस्तर तपशील:</h4>
                <p>{selectedNotice.content || 'तपशील उपलब्ध नाही.'}</p>
              </div>

              {/* Attachment Preview Section */}
              {selectedNotice.attachment_url && (
                <div className="modal-attachment-section">
                  <h4>📎 जोडलेली अधिकृत प्रत / दस्तऐवज:</h4>
                  {isImageFile(selectedNotice.attachment_url) ? (
                    <div className="modal-image-preview-box">
                      <img
                        src={selectedNotice.attachment_url}
                        alt={selectedNotice.title}
                        className="modal-preview-image"
                      />
                      <div className="preview-action-row">
                        <a
                          href={selectedNotice.attachment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-modal-doc"
                        >
                          🔍 मूळ छायाचित्र पहा / डाऊनलोड करा
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="modal-file-card">
                      <div className="file-info">
                        <span className="file-icon">📄</span>
                        <div>
                          <strong>अधिकृत परिपत्रक (PDF / Document)</strong>
                          <p>ग्रामपंचायतीद्वारे स्वाक्षरी केलेले मूळ दस्तऐवज डाऊनलोड करून वाचा.</p>
                        </div>
                      </div>
                      <a
                        href={selectedNotice.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-modal-doc"
                      >
                        📥 परिपत्रक उघडा / डाऊनलोड करा
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Actions Footer */}
            <div className="modal-footer">
              <div className="modal-footer-left">
                <button
                  className="btn-modal-share"
                  onClick={() => handleWhatsAppShare(selectedNotice)}
                >
                  📲 WhatsApp वर पाठवा
                </button>
                <button
                  className="btn-modal-print"
                  onClick={() => window.print()}
                >
                  🖨️ प्रिंट
                </button>
              </div>
              <button
                className="btn-modal-close"
                onClick={() => setSelectedNotice(null)}
              >
                बंद करा
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
